import { getApps } from "firebase/app";
import { getFirebaseServices } from "@/services/firebase/config";
import { getFunction } from "@/services/firebase/helpers";
import { Platform } from "react-native";
import { getVersion } from "./version";
import { ILogSpaEntry, ILogSpaBatchArgs, getUtf8ByteLength } from "common247";
export { getUtf8ByteLength };

/** Maximum number of log entries maintained in client memory. */
export const MAX_QUEUE_SIZE = 1000;

/** Maximum total queue memory in bytes (1MB) before oldest entries are pruned. */
export const MAX_QUEUE_BYTES = 1024 * 1024;

/** Interval between periodic flushes in milliseconds (5 minutes). */
export const FLUSH_INTERVAL_MS = 5 * 60 * 1000;

/** Maximum allowable batch payload in bytes (80KB), safely below backend 100KB ceiling. */
export const MAX_BATCH_BYTES = 80 * 1024;

/** Maximum number of log items to transmit in a single callable request batch. */
export const MAX_BATCH_LOG_COUNT = 50;

/** Maximum character size for an individual message or stack trace before truncation (35KB). */
export const MAX_SINGLE_MESSAGE_BYTES = 35 * 1024;

/** Maximum number of consecutive network/server failures before dropping a poison batch. */
export const MAX_CONSECUTIVE_FAILURES = 3;

/** Milliseconds to pause flushes when encountering backend rate limiting (defaults to 60s window). */
export const DEFAULT_RATE_LIMIT_BACKOFF_MS = 60 * 1000;

/** Maximum number of log items to transmit in a single flush cycle to stay comfortably under the 200 logs/min limit. */
export const MAX_LOGS_PER_FLUSH_RUN = 160;

let logQueue: ILogSpaEntry[] = [];
let flushTimeoutId: ReturnType<typeof setTimeout> | null = null;
let isFlushing = false;
let consecutiveFailures = 0;
let rateLimitUntil = 0;

/**
 * Schedules a queue flush with configurable delay (defaults to FLUSH_INTERVAL_MS).
 * Respects active rate limit cooldowns and supports exponential backoff during retry cycles.
 * @param {number} [delayMs=FLUSH_INTERVAL_MS] Delay in milliseconds
 * @returns {void}
 */
export function scheduleFlush(delayMs: number = FLUSH_INTERVAL_MS): void {
  const now = Date.now();
  const effectiveDelay = rateLimitUntil > now ? Math.max(delayMs, rateLimitUntil - now) : delayMs;

  if (flushTimeoutId !== null) {
    clearTimeout(flushTimeoutId);
    flushTimeoutId = null;
  }
  flushTimeoutId = setTimeout(() => {
    flushTimeoutId = null;
    flushQueue();
  }, effectiveDelay);
}

/**
 * Calculates the exact UTF-8 byte size of the serialized batch payload.
 *
 * @param {ILogSpaBatchArgs} args - Batch payload arguments
 * @returns {number} Size in UTF-8 bytes
 */
export function calculateBatchPayloadBytes(args: ILogSpaBatchArgs): number {
  try {
    return getUtf8ByteLength(JSON.stringify(args));
  } catch {
    return 0;
  }
}

/**
 * Sanitizes and truncates an individual log entry to prevent oversized payloads.
 * Truncates message and stack trace if they exceed MAX_SINGLE_MESSAGE_BYTES.
 *
 * @param {ILogSpaEntry} entry - Raw log entry
 * @returns {ILogSpaEntry} Sanitized and clamped log entry
 */
export function sanitizeLogEntry(entry: ILogSpaEntry): ILogSpaEntry {
  let message = entry.message ?? "";
  let stack = entry.stack;

  if (message.length > MAX_SINGLE_MESSAGE_BYTES) {
    message = message.slice(0, MAX_SINGLE_MESSAGE_BYTES) + "... [truncated]";
  }

  if (stack && stack.length > MAX_SINGLE_MESSAGE_BYTES) {
    stack = stack.slice(0, MAX_SINGLE_MESSAGE_BYTES) + "... [truncated]";
  }

  return {
    timestamp: entry.timestamp,
    level: entry.level,
    message,
    stack,
  };
}

/**
 * Estimates the memory footprint of the log queue in bytes.
 *
 * @param {ILogSpaEntry[]} [queue=logQueue] - The log queue to calculate
 * @returns {number} Approximate byte count
 */
export function getQueueByteSize(queue: ILogSpaEntry[] = logQueue): number {
  let bytes = 0;
  for (const entry of queue) {
    bytes += (entry.message ? getUtf8ByteLength(entry.message) : 0);
    bytes += (entry.stack ? getUtf8ByteLength(entry.stack) : 0);
    bytes += 64; // Overhead for level, timestamp, and object structure
  }
  return bytes;
}

/**
 * Slices the next batch chunk from the queue, ensuring the serialized payload
 * stays under MAX_BATCH_BYTES (80KB) and MAX_BATCH_LOG_COUNT (50 entries).
 *
 * @param {ILogSpaEntry[]} queue - The source log queue
 * @param {ILogSpaBatchArgs["deviceInfo"]} deviceInfo - Device metadata
 * @param {number} [maxBytes=MAX_BATCH_BYTES] - Maximum allowable batch bytes
 * @param {number} [maxCount=MAX_BATCH_LOG_COUNT] - Maximum allowable log count
 * @returns {ILogSpaEntry[]} Sliced entries fitting the constraints
 */
export function sliceBatchFromQueue(
  queue: ILogSpaEntry[],
  deviceInfo: ILogSpaBatchArgs["deviceInfo"],
  maxBytes: number = MAX_BATCH_BYTES,
  maxCount: number = MAX_BATCH_LOG_COUNT
): ILogSpaEntry[] {
  if (queue.length === 0) {
    return [];
  }

  const batch: ILogSpaEntry[] = [];
  // Calculate envelope base size with an empty logs array
  let currentBytes = calculateBatchPayloadBytes({ logs: [], deviceInfo });

  for (let i = 0; i < queue.length && batch.length < maxCount; i++) {
    let entry = queue[i];
    let entryJson = JSON.stringify(entry);
    let entryBytes = getUtf8ByteLength(entryJson) + 1; // +1 for comma separator in JSON array

    // If an individual entry alone exceeds maxBytes, clamp message/stack to fit safely
    if (entryBytes > maxBytes) {
      entry = sanitizeLogEntry({
        ...entry,
        message: (entry.message || "").slice(0, 1024 * 10) + "... [oversized entry truncated]",
        stack: entry.stack ? entry.stack.slice(0, 1024 * 5) + "... [truncated]" : undefined,
      });
      entryJson = JSON.stringify(entry);
      entryBytes = getUtf8ByteLength(entryJson) + 1;
    }

    // If adding this entry would breach maxBytes, finish chunk
    if (currentBytes + entryBytes > maxBytes) {
      break;
    }

    batch.push(entry);
    currentBytes += entryBytes;

    if (currentBytes >= maxBytes) {
      break;
    }
  }

  return batch;
}

/**
 * Adds a log to the batch queue with size clamping and memory bounds.
 *
 * @param {"log" | "info" | "warn" | "error"} level - The log level
 * @param {string} message - The log message content
 * @param {string} [stack] - Optional stack trace for error logs
 * @returns {void}
 */
export function queueLog(
  level: "log" | "info" | "warn" | "error",
  message: string,
  stack?: string
): void {
  const sanitized = sanitizeLogEntry({
    timestamp: Date.now(),
    level,
    message,
    stack,
  });

  logQueue.push(sanitized);

  // Enforce queue limits to prevent memory leak
  while (logQueue.length > MAX_QUEUE_SIZE || getQueueByteSize() > MAX_QUEUE_BYTES) {
    logQueue.shift();
  }

  if (level === "error") {
    // Debounce flush on errors: if recent flushes succeeded, flush after 1 second to batch bursts.
    // If we are currently experiencing consecutive failures, respect backoff instead of hammering.
    if (consecutiveFailures === 0) {
      scheduleFlush(1000);
    } else {
      scheduleFlush();
    }
  } else {
    scheduleFlush();
  }
}

/**
 * Queues a log entry and immediately flushes the queue to the backend.
 * Useful for critical lifecycle events (e.g. Share Intent) to ensure immediate observability.
 *
 * @param {"log" | "info" | "warn" | "error"} level - The log level
 * @param {string} message - The log message content
 * @param {string} [stack] - Optional stack trace for error logs
 * @returns {Promise<{ success: boolean; count: number; error?: any }>} The flush result
 */
export async function logAndFlush(
  level: "log" | "info" | "warn" | "error",
  message: string,
  stack?: string
): Promise<{ success: boolean; count: number; error?: any }> {
  queueLog(level, message, stack);
  return flushQueue();
}

/**
 * Attempts to flush the queued logs to the backend in safe chunks.
 * Handles lazy Firebase initialization, chunking to <= 8KB per request,
 * and poison batch shedding.
 *
 * @returns {Promise<{ success: boolean; count: number; error?: any }>} The status of the flush operation.
 */
export async function flushQueue(): Promise<{ success: boolean; count: number; error?: any }> {
  if (flushTimeoutId !== null) {
    clearTimeout(flushTimeoutId);
    flushTimeoutId = null;
  }

  if (logQueue.length === 0) {
    return { success: true, count: 0 };
  }

  const now = Date.now();
  if (rateLimitUntil > now) {
    scheduleFlush(rateLimitUntil - now);
    return {
      success: false,
      count: 0,
      error: new Error(`Rate limit cooldown active. Flush deferred for ${Math.ceil((rateLimitUntil - now) / 1000)}s`),
    };
  }

  if (isFlushing) {
    scheduleFlush();
    return { success: false, count: 0, error: new Error("Flush already in progress") };
  }

  // If Firebase is not yet initialized, delay sending until subsequent calls/triggers
  if (getApps().length === 0) {
    scheduleFlush();
    return { success: false, count: 0, error: new Error("Firebase not initialized") };
  }

  // If running in browser and client is offline, delay flush without incrementing failure drops
  if (Platform.OS === "web" && typeof navigator !== "undefined" && navigator.onLine === false) {
    scheduleFlush(30000);
    return { success: false, count: 0, error: new Error("Client is offline") };
  }

  isFlushing = true;
  let totalSentCount = 0;

  try {
    const fb = getFirebaseServices();
    if (!fb || !fb.functions) {
      scheduleFlush();
      return { success: false, count: 0, error: new Error("Firebase functions not initialized") };
    }
    const logSpaBatch = getFunction(fb, "logSpaBatchFunc", fb.regionOrCustomUrl);
    const deviceInfo = {
      os: Platform.OS,
      osVersion: String(Platform.Version),
      appVersion: getVersion(),
    };

    // Drain the queue in safe chunks <= 8KB and <= 50 logs per batch, up to MAX_LOGS_PER_FLUSH_RUN
    while (logQueue.length > 0 && totalSentCount < MAX_LOGS_PER_FLUSH_RUN) {
      const remainingQuota = MAX_LOGS_PER_FLUSH_RUN - totalSentCount;
      const countLimit = Math.min(MAX_BATCH_LOG_COUNT, remainingQuota);
      const chunk = sliceBatchFromQueue(logQueue, deviceInfo, MAX_BATCH_BYTES, countLimit);
      if (chunk.length === 0) {
        break;
      }

      const args: ILogSpaBatchArgs = {
        logs: chunk,
        deviceInfo,
      };

      try {
        await logSpaBatch(args);

        // Success: safely remove delivered entries from front of queue
        logQueue.splice(0, chunk.length);
        totalSentCount += chunk.length;
        consecutiveFailures = 0;
      } catch (batchError: any) {
        const errorMsg = String(batchError?.message || batchError || "");
        const isOversized =
          errorMsg.toLowerCase().includes("payload size limit exceeded") ||
          errorMsg.toLowerCase().includes("payload exceeds") ||
          batchError?.code === "invalid-argument";

        const isRateLimited =
          errorMsg.toLowerCase().includes("rate limit exceeded") ||
          batchError?.code === "resource-exhausted" ||
          batchError?.status === 429 ||
          batchError?.httpErrorCode?.status === 429 ||
          batchError?.details?.status === "resource-exhausted";

        if (isOversized) {
          // Immediately drop poison chunk to prevent queue poisoning and infinite retry loop
          logQueue.splice(0, chunk.length);
          console.warn(
            `[BatchLogger] Dropped ${chunk.length} oversized log entries to prevent queue poisoning: ${errorMsg}`
          );
          return {
            success: false,
            count: totalSentCount,
            error: batchError,
          };
        }

        if (isRateLimited) {
          // Rate limit exceeded: do NOT drop entries as poison.
          // Respect retryAfterMs or default to full 60-second window.
          const retryDelay =
            typeof batchError?.details?.retryAfterMs === "number" && batchError.details.retryAfterMs > 0
              ? batchError.details.retryAfterMs
              : DEFAULT_RATE_LIMIT_BACKOFF_MS;

          rateLimitUntil = Date.now() + retryDelay;
          scheduleFlush(retryDelay);
          return {
            success: totalSentCount > 0,
            count: totalSentCount,
            error: batchError,
          };
        }

        consecutiveFailures += 1;
        if (consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) {
          // Drop failing batch after repeated attempts to prevent memory leaks / stalled queue
          logQueue.splice(0, chunk.length);
          consecutiveFailures = 0;
          console.warn(
            `[BatchLogger] Dropped ${chunk.length} log entries after ${MAX_CONSECUTIVE_FAILURES} consecutive failed flush attempts: ${errorMsg}`
          );
        }

        // Exponential backoff for subsequent retry: 5s, 15s, 30s, up to FLUSH_INTERVAL_MS
        const backoffDelay = Math.min(
          FLUSH_INTERVAL_MS,
          5000 * Math.pow(2, Math.max(0, consecutiveFailures - 1))
        );
        scheduleFlush(backoffDelay);
        return {
          success: totalSentCount > 0,
          count: totalSentCount,
          error: batchError,
        };
      }
    }

    return { success: true, count: totalSentCount };
  } catch (error) {
    scheduleFlush();
    return { success: false, count: totalSentCount, error };
  } finally {
    isFlushing = false;
    if (logQueue.length > 0 && rateLimitUntil <= Date.now()) {
      const nextDelay = totalSentCount >= MAX_LOGS_PER_FLUSH_RUN ? DEFAULT_RATE_LIMIT_BACKOFF_MS : undefined;
      scheduleFlush(nextDelay);
    }
  }
}

/**
 * Gets the number of logs currently in the queue.
 * @returns {number} The log queue length
 */
export function getQueueLength(): number {
  return logQueue.length;
}

/**
 * Gets a shallow copy of the current log queue entries.
 * @returns {ILogSpaEntry[]} Copy of log queue
 */
export function getQueue(): ILogSpaEntry[] {
  return [...logQueue];
}

/**
 * Clears all pending logs and resets consecutive failure counters.
 * Primarily used in test isolation and diagnostic resetting.
 * @returns {void}
 */
export function clearQueue(): void {
  logQueue = [];
  consecutiveFailures = 0;
  rateLimitUntil = 0;
  if (flushTimeoutId !== null) {
    clearTimeout(flushTimeoutId);
    flushTimeoutId = null;
  }
}

/**
 * Returns remaining rate limit cooldown in milliseconds, or 0 if inactive.
 * @returns {number} Cooldown duration in ms
 */
export function getRateLimitCooldownMs(): number {
  return Math.max(0, rateLimitUntil - Date.now());
}
